import { useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { scrollProgress } from '../../context/ScrollContext';
import useMouseParallax from '../../hooks/useMouseParallax';

/* Pre-allocated scratch vectors — zero GC in useFrame */
const _position = new THREE.Vector3();
const _target = new THREE.Vector3();

export default function CameraRig() {
  const { update: updateParallax } = useMouseParallax(0.35);

  const curve = useMemo(() => {
    return new THREE.CatmullRomCurve3(
      [
        new THREE.Vector3(0, 0, 80),     // 0 - Deep space start
        new THREE.Vector3(-1.5, 0, 50),  // 1 - Earth / Waymaker
        new THREE.Vector3(1.5, 0, 15),   // 2 - Mars / Pulse Engine
        new THREE.Vector3(-1.8, 0, -20), // 3 - Saturn / Celestial Drift
        new THREE.Vector3(1.5, 0, -55),  // 4 - Neptune / FormCraft
        new THREE.Vector3(-1.5, 0, -90), // 5 - Volcanic / Ember Chronicles
        new THREE.Vector3(1.5, 0, -125), // 6 - Cyber / Code Forge
        new THREE.Vector3(0, 0, -158),   // 7 - Solar Gateway
      ],
      false,         // open curve
      'centripetal', // curve type prevents loops
      0.5            // tension
    );
  }, []);

  useFrame((state, delta) => {
    const t = Math.min(Math.max(scrollProgress.value || 0, 0), 1);

    // Camera position on spline
    curve.getPointAt(t, _position);

    // Look-ahead target for natural forward gaze along the trajectory
    const lookAhead = Math.min(t + 0.05, 1);
    curve.getPointAt(lookAhead, _target);

    // Subtle mouse parallax offset (desktop only)
    const parallax = updateParallax(delta);
    _position.x += parallax.x * 0.8;
    _position.y -= parallax.y * 0.5;

    // Smooth camera interpolation
    const lerpFactor = 1 - Math.pow(0.001, delta);
    state.camera.position.lerp(_position, lerpFactor);
    state.camera.lookAt(_target.x, _target.y, _target.z - 6);
  });

  return null;
}
